import { CONFIG } from '../config';
import type { Bullet } from '../entities/Bullet';
import type { Player } from '../entities/Player';

interface WarningArrow {
    direction: 'top' | 'bottom' | 'left' | 'right';
    intensity: number; // 0-1, 가까울수록 1
    position: number;  // 해당 방향에서의 위치
}

interface RendererLike {
    getCanvasWidth(): number;
    getCanvasHeight(): number;
}

/**
 * 화면 가장자리에서 접근하는 총알 경고 표시
 */
export class DangerWarning {
    private warnings: WarningArrow[] = [];
    private warningDistance: number;
    private arrowSize: number;
    private renderer: RendererLike | null = null;

    constructor() {
        this.warningDistance = CONFIG.DANGER_WARNING.DISTANCE;
        this.arrowSize = CONFIG.DANGER_WARNING.ARROW_SIZE;
    }

    setRenderer(renderer: RendererLike): void {
        this.renderer = renderer;
    }

    private getCanvasSize(): { width: number; height: number } {
        if (this.renderer) {
            return {
                width: this.renderer.getCanvasWidth(),
                height: this.renderer.getCanvasHeight()
            };
        }
        return {
            width: CONFIG.CANVAS_WIDTH,
            height: CONFIG.CANVAS_HEIGHT
        };
    }

    /**
     * 위험 경고 업데이트
     */
    update(player: Player, bullets: Bullet[]): void {
        this.warnings = [];

        const { width, height } = this.getCanvasSize();
        const margin = CONFIG.PLAY_AREA.MARGIN;
        const playerBounds = player.getBounds();

        for (const bullet of bullets) {
            if (!bullet.isAlive || bullet.hasEnteredPlayArea) continue;

            // 화면 밖에서 접근하는 총알만 체크
            const distToLeft = bullet.x - margin;
            const distToRight = (width - margin) - bullet.x;
            const distToTop = bullet.y - margin;
            const distToBottom = (height - margin) - bullet.y;

            // 가장 가까운 경계 확인
            if (bullet.x < margin && bullet.vx > 0) {
                // 왼쪽에서 접근
                if (Math.abs(distToLeft) < this.warningDistance) {
                    this.addWarning('left', bullet.y, Math.abs(distToLeft));
                }
            }
            if (bullet.x > width - margin && bullet.vx < 0) {
                // 오른쪽에서 접근
                if (Math.abs(distToRight) < this.warningDistance) {
                    this.addWarning('right', bullet.y, Math.abs(distToRight));
                }
            }
            if (bullet.y < margin && bullet.vy > 0) {
                // 위에서 접근
                if (Math.abs(distToTop) < this.warningDistance) {
                    this.addWarning('top', bullet.x, Math.abs(distToTop));
                }
            }
            if (bullet.y > height - margin && bullet.vy < 0) {
                // 아래에서 접근
                if (Math.abs(distToBottom) < this.warningDistance) {
                    this.addWarning('bottom', bullet.x, Math.abs(distToBottom));
                }
            }
        }
    }

    private addWarning(direction: 'top' | 'bottom' | 'left' | 'right', position: number, distance: number): void {
        const intensity = 1 - (distance / this.warningDistance);
        this.warnings.push({ direction, intensity, position });
    }

    /**
     * 경고 화살표 렌더링
     */
    render(ctx: CanvasRenderingContext2D): void {
        if (this.warnings.length === 0) return;

        const { width, height } = this.getCanvasSize();
        const margin = CONFIG.PLAY_AREA.MARGIN;
        const size = this.arrowSize;

        ctx.save();

        for (const warning of this.warnings) {
            const alpha = warning.intensity * 0.8;
            ctx.fillStyle = `rgba(255, 50, 50, ${alpha})`;

            ctx.beginPath();

            switch (warning.direction) {
                case 'top':
                    // 아래를 가리키는 화살표 (위쪽 경계)
                    ctx.moveTo(warning.position, margin + 5);
                    ctx.lineTo(warning.position - size / 2, margin + 5 + size);
                    ctx.lineTo(warning.position + size / 2, margin + 5 + size);
                    break;

                case 'bottom':
                    // 위를 가리키는 화살표 (아래쪽 경계)
                    ctx.moveTo(warning.position, height - margin - 5);
                    ctx.lineTo(warning.position - size / 2, height - margin - 5 - size);
                    ctx.lineTo(warning.position + size / 2, height - margin - 5 - size);
                    break;

                case 'left':
                    // 오른쪽을 가리키는 화살표 (왼쪽 경계)
                    ctx.moveTo(margin + 5, warning.position);
                    ctx.lineTo(margin + 5 + size, warning.position - size / 2);
                    ctx.lineTo(margin + 5 + size, warning.position + size / 2);
                    break;

                case 'right':
                    // 왼쪽을 가리키는 화살표 (오른쪽 경계)
                    ctx.moveTo(width - margin - 5, warning.position);
                    ctx.lineTo(width - margin - 5 - size, warning.position - size / 2);
                    ctx.lineTo(width - margin - 5 - size, warning.position + size / 2);
                    break;
            }

            ctx.closePath();
            ctx.fill();
        }

        ctx.restore();
    }

    /**
     * 현재 경고 수 반환
     */
    getWarningCount(): number {
        return this.warnings.length;
    }

    reset(): void {
        this.warnings = [];
    }
}
